<template>
  <div class="users-page">
    <!-- Header Section -->
    <div class="page-header-section">
      <div class="header-content">
        <div class="header-text">
          <h1 class="title is-2">
            <span class="icon-text">
              <span class="icon">
                <i class="bx bx-user-circle bx-lg"></i>
              </span>
              <span>Users Management</span>
            </span>
          </h1>
          <p class="subtitle is-6">Manage users, roles, and permissions across your organization</p>
        </div>
        <div class="header-actions">
          <div class="buttons">
            <button 
              v-if="filteredUsers.length > 0" 
              class="button is-light is-rounded"
              @click="exportUsers"
            >
              <span class="icon"><i class="bx bx-download"></i></span>
              <span>Export</span>
            </button>
            <button 
              class="button is-light is-rounded"
              @click="refreshUsers"
              :class="{ 'is-loading': loading }"
            >
              <span class="icon"><i class="bx bx-refresh"></i></span>
              <span>Refresh</span>
            </button>
            <button class="button is-primary is-rounded" @click="openCreateUserModal">
              <span class="icon"><i class="bx bx-plus"></i></span>
              <span>Add New User</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Statistics Dashboard -->
    <div v-if="!loading && users.length > 0" class="stats-dashboard mb-4">
      <div class="columns is-multiline">
        <div class="column is-3">
          <div class="stat-card has-background-primary-light">
            <div class="stat-content">
              <div class="stat-icon">
                <span class="icon is-medium has-text-primary">
                  <i class="bx bx-user"></i>
                </span>
              </div>
              <div class="stat-text">
                <p class="stat-value">{{ stats.total }}</p>
                <p class="stat-label">Total Users</p>
              </div>
            </div>
          </div>
        </div>
        <div class="column is-3">
          <div class="stat-card has-background-success-light">
            <div class="stat-content">
              <div class="stat-icon">
                <span class="icon is-medium has-text-success">
                  <i class="bx bx-check-circle"></i>
                </span>
              </div>
              <div class="stat-text">
                <p class="stat-value">{{ stats.active }}</p>
                <p class="stat-label">Active Users</p>
              </div>
            </div>
          </div>
        </div>
        <div class="column is-3">
          <div class="stat-card has-background-warning-light">
            <div class="stat-content">
              <div class="stat-icon">
                <span class="icon is-medium has-text-warning">
                  <i class="bx bx-pause-circle"></i>
                </span>
              </div>
              <div class="stat-text">
                <p class="stat-value">{{ stats.inactive }}</p>
                <p class="stat-label">Inactive Users</p>
              </div>
            </div>
          </div>
        </div>
        <div class="column is-3">
          <div class="stat-card has-background-info-light">
            <div class="stat-content">
              <div class="stat-icon">
                <span class="icon is-medium has-text-info">
                  <i class="bx bx-shield"></i>
                </span>
              </div>
              <div class="stat-text">
                <p class="stat-value">{{ stats.admins }}</p>
                <p class="stat-label">Administrators</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Enhanced Search and Filters -->
    <div v-if="!loading && users.length > 0" class="search-section mb-5">
      <div class="card has-modern-shadow">
        <div class="card-header">
          <p class="card-header-title">
            <span class="icon-text">
              <span class="icon">
                <i class="bx bx-search"></i>
              </span>
              <span>Search & Filters</span>
              <span v-if="hasActiveFilters" class="tag is-primary is-small ml-2">{{ activeFiltersCount }} active</span>
            </span>
          </p>
          <button class="card-header-icon" @click="toggleFilters" :title="showAdvancedFilters ? 'Hide Advanced Filters' : 'Show Advanced Filters'">
            <span class="icon">
              <i class="bx" :class="showAdvancedFilters ? 'bx-chevron-up' : 'bx-chevron-down'"></i>
            </span>
          </button>
        </div>
        <div class="card-content">
          <div class="columns is-vcentered">
            <div class="column is-6">
              <div class="field">
                <div class="control has-icons-left has-icons-right">
                  <input 
                    v-model="filters.search" 
                    class="input is-rounded" 
                    type="text" 
                    placeholder="Search by name, email, role..."
                    autocomplete="off"
                    @input="debouncedSearch"
                  >
                  <span class="icon is-small is-left">
                    <i class="bx bx-search"></i>
                  </span>
                  <span v-if="filters.search" class="icon is-small is-right is-clickable" @click="clearSearch">
                    <i class="bx bx-x"></i>
                  </span>
                </div>
              </div>
            </div>
            <div class="column is-2">
              <div class="field">
                <div class="control">
                  <div class="select is-fullwidth is-rounded">
                    <select v-model="filters.sortBy" @change="sortUsers">
                      <option value="name">Name A-Z</option>
                      <option value="email">Email A-Z</option>
                      <option value="role">Role</option>
                      <option value="created_at">Recently Added</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
            <div class="column is-2">
              <div class="field">
                <div class="control">
                  <div class="select is-fullwidth is-rounded">
                    <select v-model="viewMode">
                      <option value="cards">Card View</option>
                      <option value="table">Table View</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
            <div class="column is-2">
              <div class="field">
                <div class="control">
                  <div class="select is-fullwidth is-rounded">
                    <select v-model="perPage" @change="resetPagination">
                      <option value="12">12 per page</option>
                      <option value="24">24 per page</option>
                      <option value="48">48 per page</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Advanced Filters -->
          <transition name="fade">
            <div v-show="showAdvancedFilters" class="advanced-filters">
              <hr>
              <div class="columns">
                <div class="column is-3">
                  <div class="field">
                    <label class="label is-small">Status</label>
                    <div class="control">
                      <div class="select is-fullwidth">
                        <select v-model="filters.status" @change="filterUsers">
                          <option value="">All Users</option>
                          <option value="active">Active Only</option>
                          <option value="inactive">Inactive Only</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="column is-3">
                  <div class="field">
                    <label class="label is-small">Role</label>
                    <div class="control">
                      <div class="select is-fullwidth">
                        <select v-model="filters.role" @change="filterUsers">
                          <option value="">All Roles</option>
                          <option v-for="role in roles" :key="role.id" :value="role.id">
                            {{ role.name }}
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="column is-3">
                  <div class="field">
                    <label class="label is-small">Last Login</label>
                    <div class="control">
                      <div class="select is-fullwidth">
                        <select v-model="filters.loginStatus" @change="filterUsers">
                          <option value="">All Users</option>
                          <option value="recent">Recent (7 days)</option>
                          <option value="inactive">Inactive (30+ days)</option>
                          <option value="never">Never logged in</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="column is-3">
                  <div class="field">
                    <label class="label is-small">Actions</label>
                    <div class="control">
                      <button class="button is-light is-fullwidth" @click="clearFilters">
                        <span class="icon"><i class="bx bx-refresh"></i></span>
                        <span>Reset Filters</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>

    <!-- Bulk Actions -->
    <div v-if="selectedUsers.length > 0" class="bulk-actions-section mb-4">
      <div class="notification is-info is-light">
        <div class="columns is-vcentered">
          <div class="column">
            <strong>{{ selectedUsers.length }}</strong> user(s) selected
          </div>
          <div class="column is-narrow">
            <div class="buttons">
              <button class="button is-success is-small" @click="bulkActivateUsers">
                <span class="icon is-small">
                  <i class="bx bx-check-circle"></i>
                </span>
                <span>Activate</span>
              </button>
              <button class="button is-warning is-small" @click="bulkDeactivateUsers">
                <span class="icon is-small">
                  <i class="bx bx-pause-circle"></i>
                </span>
                <span>Deactivate</span>
              </button>
              <button class="button is-danger is-small" @click="bulkDeleteUsers">
                <span class="icon is-small">
                  <i class="bx bx-trash"></i>
                </span>
                <span>Delete</span>
              </button>
              <button class="button is-light is-small" @click="clearSelection">
                <span>Clear Selection</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Users List/Cards -->
    <div class="users-content">
      <!-- View Toggle -->
      <div class="view-controls mb-4">
        <div class="field is-grouped is-grouped-right">
          <div class="control">
            <div class="buttons has-addons">
              <button 
                class="button is-small"
                :class="{ 'is-primary': viewMode === 'cards' }"
                @click="viewMode = 'cards'"
              >
                <span class="icon is-small">
                  <i class="bx bx-grid-alt"></i>
                </span>
                <span>Cards</span>
              </button>
              <button 
                class="button is-small"
                :class="{ 'is-primary': viewMode === 'table' }"
                @click="viewMode = 'table'"
              >
                <span class="icon is-small">
                  <i class="bx bx-list-ul"></i>
                </span>
                <span>Table</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="has-text-centered py-6">
        <div class="loader-container">
          <progress class="progress is-primary" max="100"></progress>
          <p class="has-text-grey mt-4">Loading users...</p>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else-if="filteredUsers.length === 0" class="empty-state has-text-centered">
        <div class="empty-state-content">
          <div class="empty-state-icon mb-4">
            <i class="bx bx-user-x bx-lg"></i>
          </div>
          <h3 class="title is-5">
            {{ filters.search || hasActiveFilters ? 'No users found' : 'No users yet' }}
          </h3>
          <p class="subtitle">
            {{ filters.search || hasActiveFilters ? 
                'Try adjusting your search criteria or filters.' : 
                'Get started by adding your first user.' }}
          </p>
          <div class="buttons is-centered mt-4">
            <button 
              v-if="filters.search || hasActiveFilters"
              class="button is-light"
              @click="clearFilters"
            >
              Clear Filters
            </button>
            <button 
              v-else
              class="button is-primary"
              @click="openCreateUserModal"
            >
              <span class="icon">
                <i class="bx bx-plus"></i>
              </span>
              <span>Add First User</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Cards View -->
      <div v-else-if="viewMode === 'cards'" class="users-cards">
        <div class="columns is-multiline">
          <div 
            v-for="user in paginatedUsers" 
            :key="user.id" 
            class="column is-4-desktop is-6-tablet"
          >
            <div class="user-card" :class="{ 
              'is-selected': isSelected(user.id),
              'is-disabled': !user.is_active
            }">
              <div class="user-card-header">
                <div class="user-card-checkbox">
                  <label class="checkbox">
                    <input 
                      type="checkbox" 
                      :checked="isSelected(user.id)"
                      @change="toggleUserSelection(user.id)"
                    >
                  </label>
                </div>
                <div class="user-card-status">
                  <span 
                    class="tag is-small"
                    :class="user.is_active ? 'is-success' : 'is-warning'"
                  >
                    {{ user.is_active ? 'Active' : 'Inactive' }}
                  </span>
                </div>
              </div>

              <div class="user-card-content">
                <div class="user-header">
                  <div class="user-avatar-icon">
                    <span class="icon is-large" :class="{
                      'has-text-primary': user.is_active,
                      'has-text-grey': !user.is_active,
                      'has-text-warning': user.is_superadmin,
                      'has-text-info': user.is_admin && !user.is_superadmin
                    }">
                      <i class="bx bx-user-circle bx-lg"></i>
                    </span>
                    <div v-if="user.is_superadmin" class="user-badge superadmin-badge">
                      <span class="icon is-small">
                        <i class="bx bx-crown"></i>
                      </span>
                    </div>
                    <div v-else-if="user.is_admin" class="user-badge admin-badge">
                      <span class="icon is-small">
                        <i class="bx bx-star"></i>
                      </span>
                    </div>
                  </div>
                  <div class="user-basic-info">
                    <h4 class="user-name">
                      {{ user.first_name }}
                      <span v-if="user.middle_name" class="middle-name">{{ user.middle_name }}</span>
                      {{ user.last_name }}
                    </h4>
                    <p class="user-email">{{ user.email }}</p>
                  </div>
                </div>

                <div class="user-details">
                  <!-- Role and Status -->
                  <div class="user-detail-row">
                    <span class="detail-label">
                      <span class="icon is-small">
                        <i class="bx bx-shield-quarter"></i>
                      </span>
                      Role:
                    </span>
                    <span class="detail-value">
                      <span class="tag is-info is-light is-small">
                        {{ user.role?.name || 'No Role' }}
                      </span>
                    </span>
                  </div>

                  <div class="user-detail-row">
                    <span class="detail-label">
                      <span class="icon is-small">
                        <i class="bx bx-check-circle"></i>
                      </span>
                      Status:
                    </span>
                    <span class="detail-value">
                      <span class="tag is-small" :class="user.is_active ? 'is-success' : 'is-danger'">
                        {{ user.is_active ? 'Active Account' : 'Disabled Account' }}
                      </span>
                    </span>
                  </div>

                  <!-- Contact Information -->
                  <div v-if="user.phone" class="user-detail-row">
                    <span class="detail-label">
                      <span class="icon is-small">
                        <i class="bx bx-phone"></i>
                      </span>
                      Phone:
                    </span>
                    <span class="detail-value is-size-7">
                      {{ user.phone }}
                    </span>
                  </div>

                  <!-- Address Information -->
                  <div v-if="user.address" class="user-detail-row">
                    <span class="detail-label">
                      <span class="icon is-small">
                        <i class="bx bx-map"></i>
                      </span>
                      Address:
                    </span>
                    <span class="detail-value is-size-7">
                      {{ user.address }}
                    </span>
                  </div>

                  <div class="user-detail-row" v-if="user.city || user.zip_code">
                    <span class="detail-label">
                      <span class="icon is-small">
                        <i class="bx bx-current-location"></i>
                      </span>
                      Location:
                    </span>
                    <span class="detail-value is-size-7">
                      <span v-if="user.city">{{ user.city }}</span>
                      <span v-if="user.city && user.zip_code">, </span>
                      <span v-if="user.zip_code">{{ user.zip_code }}</span>
                    </span>
                  </div>

                  <!-- Membership Info -->
                  <div class="user-detail-row">
                    <span class="detail-label">
                      <span class="icon is-small">
                        <i class="bx bx-calendar"></i>
                      </span>
                      Joined:
                    </span>
                    <span class="detail-value is-size-7">
                      {{ formatDate(user.created_at) }}
                    </span>
                  </div>
                </div>
              </div>

              <div class="user-card-actions">
                <div class="buttons is-centered">
                  <button 
                    class="button is-small is-primary"
                    @click="editUser(user)"
                    title="Edit User"
                  >
                    <span class="icon is-small">
                      <i class="bx bx-edit"></i>
                    </span>
                    <span>Edit</span>
                  </button>
                  <button 
                    class="button is-small is-info"
                    @click="resetUserPassword(user)"
                    title="Reset Password"
                  >
                    <span class="icon is-small">
                      <i class="bx bx-key"></i>
                    </span>
                    <span>Reset Password</span>
                  </button>
                  <button 
                    v-if="!user.is_active || !isCurrentUser(user)"
                    class="button is-small"
                    :class="user.is_active ? 'is-warning' : 'is-success'"
                    @click="toggleUserStatus(user)"
                    :title="user.is_active ? 'Disable User' : 'Enable User'"
                  >
                    <span class="icon is-small">
                      <i :class="user.is_active ? 'bx bx-pause-circle' : 'bx bx-play-circle'"></i>
                    </span>
                    <span>{{ user.is_active ? 'Disable' : 'Enable' }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Table View -->
      <div v-else class="users-table">
        <div class="table-container">
          <table class="table is-fullwidth is-hoverable">
            <thead>
              <tr>
                <th width="50">
                  <label class="checkbox">
                    <input 
                      type="checkbox"
                      :checked="isAllSelected"
                      @change="toggleAllSelection"
                    >
                  </label>
                </th>
                <th>User</th>
                <th>Role</th>
                <th>Status</th>
                <th>Phone</th>
                <th>Address</th>
                <th>Location</th>
                <th>Last Login</th>
                <th>Joined</th>
                <th width="150">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr 
                v-for="user in paginatedUsers" 
                :key="user.id"
                :class="{ 'is-selected': isSelected(user.id) }"
              >
                <td>
                  <label class="checkbox">
                    <input 
                      type="checkbox" 
                      :checked="isSelected(user.id)"
                      @change="toggleUserSelection(user.id)"
                    >
                  </label>
                </td>
                <td>
                  <div class="user-cell">
                    <div class="user-avatar-table mr-3">
                      <span class="icon is-medium" :class="{
                        'has-text-primary': user.is_active,
                        'has-text-grey': !user.is_active,
                        'has-text-warning': user.is_superadmin,
                        'has-text-info': user.is_admin && !user.is_superadmin
                      }">
                        <i class="bx bx-user-circle bx-md"></i>
                      </span>
                      <div v-if="user.is_superadmin" class="user-badge-small superadmin-badge-small">
                        <i class="bx bx-crown"></i>
                      </div>
                      <div v-else-if="user.is_admin" class="user-badge-small admin-badge-small">
                        <i class="bx bx-star"></i>
                      </div>
                    </div>
                    <div>
                      <div class="user-name">{{ user.first_name }} {{ user.last_name }}</div>
                      <div class="user-email">{{ user.email }}</div>
                    </div>
                  </div>
                </td>
                <td>
                  <span class="tag is-info is-light">
                    {{ user.role?.name || 'No Role' }}
                  </span>
                </td>
                <td>
                  <span 
                    class="tag"
                    :class="user.is_active ? 'is-success' : 'is-warning'"
                  >
                    {{ user.is_active ? 'Active' : 'Inactive' }}
                  </span>
                </td>
                <td>
                  <span v-if="user.phone" class="is-size-7">
                    {{ user.phone }}
                  </span>
                  <span v-else class="has-text-grey-light">-</span>
                </td>
                <td>
                  <span v-if="user.address" class="is-size-7">
                    {{ user.address }}
                  </span>
                  <span v-else class="has-text-grey-light">-</span>
                </td>
                <td>
                  <span v-if="user.city || user.zip_code" class="is-size-7">
                    <span v-if="user.city">{{ user.city }}</span>
                    <span v-if="user.city && user.zip_code">, </span>
                    <span v-if="user.zip_code">{{ user.zip_code }}</span>
                  </span>
                  <span v-else class="has-text-grey-light">-</span>
                </td>
                <td>
                  <span v-if="user.last_login_at" class="is-size-7 has-text-grey">
                    {{ formatTimeAgo(user.last_login_at) }}
                  </span>
                  <span v-else class="has-text-grey-light">Never</span>
                </td>
                <td>
                  <span class="is-size-7 has-text-grey">
                    {{ formatDate(user.created_at) }}
                  </span>
                </td>
                <td>
                  <div class="buttons is-small">
                    <button 
                      class="button is-small is-primary"
                      @click="editUser(user)"
                      title="Edit User"
                    >
                      <span class="icon is-small">
                        <i class="bx bx-edit"></i>
                      </span>
                      <span>Edit</span>
                    </button>
                    <button 
                      class="button is-small is-info"
                      @click="resetUserPassword(user)"
                      title="Reset Password"
                    >
                      <span class="icon is-small">
                        <i class="bx bx-key"></i>
                      </span>
                      <span>Reset Password</span>
                    </button>
                    <button 
                      v-if="!user.is_active || !isCurrentUser(user)"
                      class="button is-small"
                      :class="user.is_active ? 'is-warning' : 'is-success'"
                      @click="toggleUserStatus(user)"
                      :title="user.is_active ? 'Disable User' : 'Enable User'"
                    >
                      <span class="icon is-small">
                        <i :class="user.is_active ? 'bx bx-pause-circle' : 'bx bx-play-circle'"></i>
                      </span>
                      <span>{{ user.is_active ? 'Disable' : 'Enable' }}</span>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="pagination-section mt-5">
        <nav class="pagination is-centered" role="navigation">
          <button 
            class="pagination-previous"
            :disabled="currentPage === 1"
            @click="goToPage(currentPage - 1)"
          >
            Previous
          </button>
          <button 
            class="pagination-next"
            :disabled="currentPage === totalPages"
            @click="goToPage(currentPage + 1)"
          >
            Next
          </button>
          <ul class="pagination-list">
            <li v-for="page in visiblePages" :key="page">
              <button 
                class="pagination-link"
                :class="{ 'is-current': page === currentPage }"
                @click="goToPage(page)"
                v-if="typeof page === 'number'"
              >
                {{ page }}
              </button>
              <span v-else class="pagination-ellipsis">&hellip;</span>
            </li>
          </ul>
        </nav>

        <div class="pagination-info has-text-centered mt-3">
          <p class="is-size-7 has-text-grey">
            Showing {{ startIndex + 1 }} to {{ endIndex }} of {{ filteredUsers.length }} users
          </p>
        </div>
      </div>
    </div>

    <!-- Password Reset Modal -->
    <div class="modal" :class="{ 'is-active': showPasswordResetModal }">
      <div class="modal-background" @click="closePasswordResetModal"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">
            <span class="icon-text">
              <span class="icon has-text-info">
                <i class="bx bx-key"></i>
              </span>
              <span>Reset Password</span>
            </span>
          </p>
          <button class="delete" @click="closePasswordResetModal"></button>
        </header>
        <section class="modal-card-body">
          <div class="field">
            <label class="label">User</label>
            <div class="control">
              <input 
                class="input" 
                type="text" 
                :value="selectedUser ? `${selectedUser.first_name} ${selectedUser.last_name} (${selectedUser.email})` : ''"
                readonly
              >
            </div>
          </div>

          <div class="field">
            <label class="label">New Password</label>
            <div class="control has-icons-left">
              <input 
                v-model="newPassword" 
                class="input" 
                type="text" 
                placeholder="Enter new password"
                autocomplete="new-password"
                @keyup.enter="confirmPasswordReset"
              >
              <span class="icon is-small is-left">
                <i class="bx bx-edit"></i>
              </span>
            </div>
            <p class="help">Enter a strong password for the user</p>
          </div>
        </section>
        <footer class="modal-card-foot">
          <button 
            class="button is-success" 
            @click="confirmPasswordReset"
            :disabled="!newPassword || isResettingPassword"
            :class="{ 'is-loading': isResettingPassword }"
          >
            <span class="icon">
              <i class="bx bx-check"></i>
            </span>
            <span>Reset Password</span>
          </button>
          <button class="button" @click="closePasswordResetModal">Cancel</button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import axios from 'axios'
import { formatDistanceToNow, format } from 'date-fns'

// Debounce utility function
function debounce(func, wait) {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

export default {
  name: 'UsersIndex',
  setup() {
    const router = useRouter()
    const store = useStore()

    // Reactive data
    const loading = ref(false)
    const users = ref([])
    const roles = ref([])
    const selectedUsers = ref([])
    const viewMode = ref('cards')
    const currentPage = ref(1)
    const perPage = ref(12)
    const showAdvancedFilters = ref(false)

    // Password reset modal
    const showPasswordResetModal = ref(false)
    const selectedUser = ref(null)
    const newPassword = ref('')
    const isResettingPassword = ref(false)

    // Filters
    const filters = reactive({
      search: '',
      role: '',
      status: '',
      loginStatus: '',
      sortBy: 'name'
    })

    // Statistics
    const stats = reactive({
      total: 0,
      active: 0,
      inactive: 0,
      admins: 0
    })

    // Computed properties
    const filteredUsers = computed(() => {
      let result = [...users.value]

      // Search filter
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase()
        result = result.filter(user => 
          user.full_name?.toLowerCase().includes(searchTerm) ||
          user.email?.toLowerCase().includes(searchTerm) ||
          user.first_name?.toLowerCase().includes(searchTerm) ||
          user.last_name?.toLowerCase().includes(searchTerm)
        )
      }

      // Role filter
      if (filters.role) {
        result = result.filter(user => user.role?.id === parseInt(filters.role))
      }

      // Status filter
      if (filters.status) {
        const isActive = filters.status === 'active'
        result = result.filter(user => user.is_active === isActive)
      }

      // Sort
      result.sort((a, b) => {
        switch (filters.sortBy) {
          case 'name':
            return (a.full_name || '').localeCompare(b.full_name || '')
          case 'email':
            return (a.email || '').localeCompare(b.email || '')
          case 'role':
            return (a.role?.name || '').localeCompare(b.role?.name || '')
          case 'created_at':
            return new Date(b.created_at || 0) - new Date(a.created_at || 0)
          default:
            return 0
        }
      })

      return result
    })

    const paginatedUsers = computed(() => {
      const start = (currentPage.value - 1) * perPage.value
      const end = start + perPage.value
      return filteredUsers.value.slice(start, end)
    })

    const totalPages = computed(() => {
      return Math.ceil(filteredUsers.value.length / perPage.value)
    })

    const startIndex = computed(() => {
      return (currentPage.value - 1) * perPage.value
    })

    const endIndex = computed(() => {
      return Math.min(startIndex.value + perPage.value, filteredUsers.value.length)
    })

    const visiblePages = computed(() => {
      const pages = []
      const total = totalPages.value
      const current = currentPage.value

      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i)
        }
      } else {
        if (current <= 4) {
          for (let i = 1; i <= 5; i++) pages.push(i)
          pages.push('...')
          pages.push(total)
        } else if (current >= total - 3) {
          pages.push(1)
          pages.push('...')
          for (let i = total - 4; i <= total; i++) pages.push(i)
        } else {
          pages.push(1)
          pages.push('...')
          for (let i = current - 1; i <= current + 1; i++) pages.push(i)
          pages.push('...')
          pages.push(total)
        }
      }

      return pages
    })

    const hasActiveFilters = computed(() => {
      return filters.search || filters.role || filters.status || filters.loginStatus
    })

    const activeFiltersCount = computed(() => {
      let count = 0
      if (filters.search) count++
      if (filters.role) count++
      if (filters.status) count++
      if (filters.loginStatus) count++
      return count
    })

    const toggleFilters = () => {
      showAdvancedFilters.value = !showAdvancedFilters.value
    }

    const resetPagination = () => {
      currentPage.value = 1
    }

    const isAllSelected = computed(() => {
      return paginatedUsers.value.length > 0 && 
             selectedUsers.value.length === paginatedUsers.value.length &&
             paginatedUsers.value.every(user => selectedUsers.value.includes(user.id))
    })

    // Methods
    const loadUsers = async () => {
      try {
        loading.value = true
        const response = await axios.get('/api/users', {
          params: {
            include: 'role'
          }
        })

        users.value = response.data.data || response.data
        calculateStats()

      } catch (error) {
        console.error('Error loading users:', error)
      } finally {
        loading.value = false
      }
    }

    const loadRoles = async () => {
      try {
        const response = await axios.get('/api/roles')
        roles.value = response.data.data || response.data
      } catch (error) {
        console.error('Error loading roles:', error)
      }
    }

    const calculateStats = () => {
      stats.total = users.value.length
      stats.active = users.value.filter(user => user.is_active).length
      stats.inactive = users.value.filter(user => !user.is_active).length
      stats.admins = users.value.filter(user => 
        user.role && (user.role.name?.toLowerCase().includes('admin') || user.is_admin)
      ).length
    }

    const refreshUsers = () => {
      loadUsers()
    }

    const debouncedSearch = debounce(() => {
      currentPage.value = 1
      filterUsers()
    }, 300)

    const filterUsers = () => {
      currentPage.value = 1
    }

    const clearSearch = () => {
      filters.search = ''
      filterUsers()
    }

    const clearFilters = () => {
      filters.search = ''
      filters.role = ''
      filters.status = ''
      currentPage.value = 1
    }

    const sortUsers = () => {
      currentPage.value = 1
    }

    const getRoleName = (roleId) => {
      const role = roles.value.find(r => r.id === parseInt(roleId))
      return role ? role.name : 'Unknown'
    }

    const currentUser = computed(() => store.getters['auth/currentUser'])

    const isCurrentUser = (user) => {
      return currentUser.value && currentUser.value.id === user.id
    }

    // Selection methods
    const isSelected = (userId) => {
      return selectedUsers.value.includes(userId)
    }

    const toggleUserSelection = (userId) => {
      const index = selectedUsers.value.indexOf(userId)
      if (index > -1) {
        selectedUsers.value.splice(index, 1)
      } else {
        selectedUsers.value.push(userId)
      }
    }

    const toggleAllSelection = () => {
      if (isAllSelected.value) {
        selectedUsers.value = selectedUsers.value.filter(
          id => !paginatedUsers.value.find(user => user.id === id)
        )
      } else {
        const newSelections = paginatedUsers.value
          .map(user => user.id)
          .filter(id => !selectedUsers.value.includes(id))
        selectedUsers.value.push(...newSelections)
      }
    }

    const clearSelection = () => {
      selectedUsers.value = []
    }

    // User actions
    const openCreateUserModal = () => {
      router.push('/users/create')
    }

    const viewUser = (user) => {
      router.push(`/users/${user.id}`)
    }

    const editUser = (user) => {
      router.push(`/users/${user.id}/edit`)
    }

    const toggleUserStatus = async (user) => {
      // Prevent users from disabling their own account
      if (currentUser.value && currentUser.value.id === user.id && user.is_active) {
        store.dispatch('notifications/showNotification', {
          message: 'You cannot disable your own account',
          type: 'warning',
          timeout: 3000
        })
        return
      }

      try {
        await axios.patch(`/api/users/${user.id}/toggle-active`)

        user.is_active = !user.is_active
        calculateStats()

      } catch (error) {
        console.error('Error toggling user status:', error)
        store.dispatch('notifications/showNotification', {
          message: 'Error updating user status',
          type: 'danger',
          timeout: 3000
        })
      }
    }

    const resetUserPassword = (user) => {
      selectedUser.value = user
      newPassword.value = ''
      showPasswordResetModal.value = true
    }

    const closePasswordResetModal = () => {
      showPasswordResetModal.value = false
      selectedUser.value = null
      newPassword.value = ''
      isResettingPassword.value = false
    }

    const confirmPasswordReset = async () => {
      if (!newPassword.value) {
        return
      }

      isResettingPassword.value = true

      try {
        await axios.post(`/api/users/${selectedUser.value.id}/reset-password`, {
          new_password: newPassword.value
        })

        store.dispatch('notifications/showNotification', {
          message: `Password reset successfully for ${selectedUser.value.first_name} ${selectedUser.value.last_name}!`,
          type: 'success',
          timeout: 5000
        })
        closePasswordResetModal()

      } catch (error) {
        console.error('Error resetting password:', error)
        const errorMessage = error.response?.data?.errors?.new_password?.[0] || 
                           error.response?.data?.message || 
                           error.message
        store.dispatch('notifications/showNotification', {
          message: 'Error resetting password: ' + errorMessage,
          type: 'danger',
          timeout: 5000
        })
      } finally {
        isResettingPassword.value = false
      }
    }

    const deleteUser = async (user) => {
      if (!confirm(`Are you sure you want to delete ${user.full_name}? This action cannot be undone.`)) {
        return
      }

      try {
        await axios.delete(`/api/users/${user.id}`)

        const index = users.value.findIndex(u => u.id === user.id)
        if (index > -1) {
          users.value.splice(index, 1)
        }

        const selectedIndex = selectedUsers.value.indexOf(user.id)
        if (selectedIndex > -1) {
          selectedUsers.value.splice(selectedIndex, 1)
        }

        calculateStats()

      } catch (error) {
        console.error('Error deleting user:', error)
      }
    }

    // Bulk actions
    const bulkActivateUsers = async () => {
      if (!confirm(`Activate ${selectedUsers.value.length} selected users?`)) return

      try {
        await axios.patch('/api/users/bulk-activate', {
          user_ids: selectedUsers.value
        })

        selectedUsers.value.forEach(userId => {
          const user = users.value.find(u => u.id === userId)
          if (user) user.is_active = true
        })

        calculateStats()
        clearSelection()

      } catch (error) {
        console.error('Error bulk activating users:', error)
      }
    }

    const bulkDeactivateUsers = async () => {
      if (!confirm(`Deactivate ${selectedUsers.value.length} selected users?`)) return

      try {
        await axios.patch('/api/users/bulk-deactivate', {
          user_ids: selectedUsers.value
        })

        selectedUsers.value.forEach(userId => {
          const user = users.value.find(u => u.id === userId)
          if (user) user.is_active = false
        })

        calculateStats()
        clearSelection()

      } catch (error) {
        console.error('Error bulk deactivating users:', error)
      }
    }

    const bulkDeleteUsers = async () => {
      if (!confirm(`Are you sure you want to delete ${selectedUsers.value.length} selected users? This action cannot be undone.`)) {
        return
      }

      try {
        await axios.delete('/api/users/bulk-delete', {
          data: { user_ids: selectedUsers.value }
        })

        users.value = users.value.filter(user => !selectedUsers.value.includes(user.id))

        calculateStats()
        clearSelection()

      } catch (error) {
        console.error('Error bulk deleting users:', error)
      }
    }

    const exportUsers = () => {
      const csvContent = generateCSV(filteredUsers.value)
      downloadCSV(csvContent, 'users-export.csv')
    }

    const generateCSV = (data) => {
      const headers = ['Name', 'Email', 'Role', 'Status', 'Joined', 'Last Login']
      const rows = data.map(user => [
        user.full_name || '',
        user.email || '',
        user.role?.name || 'No Role',
        user.is_active ? 'Active' : 'Inactive',
        formatDate(user.created_at),
        user.last_login_at ? formatDate(user.last_login_at) : 'Never'
      ])

      return [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n')
    }

    const downloadCSV = (content, filename) => {
      const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', filename)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }

    // Pagination
    const goToPage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page
      }
    }

    // Utility methods
    const formatDate = (date) => {
      if (!date) return 'N/A'
      return format(new Date(date), 'MMM dd, yyyy')
    }

    const formatTimeAgo = (date) => {
      if (!date) return 'Never'
      return formatDistanceToNow(new Date(date), { addSuffix: true })
    }

    // Watch for filter changes
    watch(() => filteredUsers.value.length, () => {
      if (currentPage.value > totalPages.value) {
        currentPage.value = Math.max(1, totalPages.value)
      }
    })

    // Initialize
    onMounted(() => {
      loadUsers()
      loadRoles()
    })

    return {
      // Reactive data
      loading,
      users,
      roles,
      selectedUsers,
      viewMode,
      currentPage,
      perPage,
      showAdvancedFilters,
      showPasswordResetModal,
      selectedUser,
      newPassword,
      isResettingPassword,
      filters,
      stats,
      store,

      // Computed
      currentUser,
      filteredUsers,
      paginatedUsers,
      totalPages,
      startIndex,
      endIndex,
      visiblePages,
      hasActiveFilters,
      activeFiltersCount,
      isAllSelected,

      // Methods
      toggleFilters,
      resetPagination,
      closePasswordResetModal,
      confirmPasswordReset,

      // Methods
      loadUsers,
      refreshUsers,
      debouncedSearch,
      filterUsers,
      clearSearch,
      clearFilters,
      sortUsers,
      getRoleName,
      isCurrentUser,
      isSelected,
      toggleUserSelection,
      toggleAllSelection,
      clearSelection,
      openCreateUserModal,
      viewUser,
      editUser,
      toggleUserStatus,
      resetUserPassword,
      deleteUser,
      bulkActivateUsers,
      bulkDeactivateUsers,
      bulkDeleteUsers,
      exportUsers,
      goToPage,
      formatDate,
      formatTimeAgo
    }
  }
}
</script>

<style lang="scss" scoped>
.users-page {
  // Header Section
  .page-header-section {
    margin-bottom: 2rem;

    .header-content {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 1.5rem 0;
      border-bottom: 1px solid #e5e7eb;

      .header-text {
        .title {
          margin-bottom: 0.5rem;
          color: #1f2937;

          .icon {
            color: #667eea;
          }
        }

        .subtitle {
          color: #6b7280;
          margin-bottom: 0;
        }
      }

      .header-actions {
        .buttons {
          margin-bottom: 0;

          .button {
            background: white;
            border: 1px solid #d1d5db;
            color: #374151;

            &.is-rounded {
              border-radius: 1.5rem;
              padding-left: 1.25rem;
              padding-right: 1.25rem;
            }

            &.is-primary {
              border-color: #667eea;
              color: #667eea;

              &:hover {
                background-color: #667eea;
                color: white;
                transform: translateY(-1px);
                box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
              }
            }

            &.is-light {
              border-color: #d1d5db;

              &:hover {
                transform: translateY(-1px);
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                border-color: #9ca3af;
              }
            }
          }
        }
      }
    }
  }

  // Statistics Dashboard
  .stats-dashboard {
    .stat-card {
      background: white;
      border-radius: 0.75rem;
      padding: 1rem;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      transition: all 0.2s ease;

      &:hover {
        transform: translateY(-1px);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      }

      &.has-background-primary-light {
        border-left: 4px solid #667eea;

        .stat-text {
          .stat-value {
            color: #667eea;
          }
        }
      }

      &.has-background-success-light {
        border-left: 4px solid #10b981;

        .stat-text {
          .stat-value {
            color: #10b981;
          }
        }
      }

      &.has-background-warning-light {
        border-left: 4px solid #f59e0b;

        .stat-text {
          .stat-value {
            color: #f59e0b;
          }
        }
      }

      &.has-background-info-light {
        border-left: 4px solid #06b6d4;

        .stat-text {
          .stat-value {
            color: #06b6d4;
          }
        }
      }

      .stat-content {
        display: flex;
        align-items: center;

        .stat-icon {
          margin-right: 0.75rem;
        }

        .stat-text {
          .stat-value {
            font-size: 1.5rem;
            font-weight: 700;
            margin-bottom: 0.125rem;
            line-height: 1.2;
            color: #1f2937;
          }

          .stat-label {
            font-size: 0.75rem;
            color: #374151;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.05em;
          }
        }
      }
    }
  }

  // Search Section
  .search-section {
    .card {
      border-radius: 1rem;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

      &.has-modern-shadow {
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
      }

      .card-header {
        border-bottom: 1px solid #e5e7eb;

        .card-header-title {
          color: #1f2937;
          font-weight: 600;
        }

        .card-header-icon:hover {
          background-color: #f3f4f6;
          border-radius: 0.375rem;
        }
      }

      .advanced-filters {
        .label {
          color: #374151;
          font-weight: 600;
        }
      }
    }
  }

  // Fade transition
  .fade-enter-active, .fade-leave-active {
    transition: opacity 0.3s ease;
  }
  .fade-enter-from, .fade-leave-to {
    opacity: 0;
  }

  // Password Reset Modal
  .modal {
    .modal-card {
      max-width: 500px;
      border-radius: 12px;

      .modal-card-head {
        background-color: #f8f9fa;
        border-bottom: 1px solid #e9ecef;
        border-radius: 12px 12px 0 0;

        .modal-card-title {
          color: #495057;
          font-weight: 600;
        }
      }

      .modal-card-body {
        padding: 2rem;

        .field {
          margin-bottom: 1.5rem;

          .label {
            color: #495057;
            font-weight: 600;
            margin-bottom: 0.5rem;
          }

          .input {
            border-radius: 8px;
            border: 1px solid #d1d5db;

            &:focus {
              border-color: #667eea;
              box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
            }

            &.is-danger {
              border-color: #ef4444;
            }

            &[readonly] {
              background-color: #f8f9fa;
              color: #6c757d;
            }
          }

          .help {
            font-size: 0.8rem;
            margin-top: 0.25rem;

            &.is-danger {
              color: #ef4444;
            }
          }
        }
      }

      .modal-card-foot {
        background-color: #f8f9fa;
        border-top: 1px solid #e9ecef;
        border-radius: 0 0 12px 12px;
        padding: 1.5rem;

        .button {
          border-radius: 8px;

          &.is-success {
            background: linear-gradient(135deg, #10b981 0%, #059669 100%);
            border: none;

            &:hover:not(:disabled) {
              transform: translateY(-1px);
              box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
            }

            &:disabled {
              opacity: 0.6;
              cursor: not-allowed;
            }
          }
        }
      }
    }
  }

  // Statistics Cards
  .stats-section {
    .stat-card {
      background: white;
      border-radius: 16px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
      transition: all 0.3s ease;
      height: 100%;
      position: relative;
      overflow: hidden;

      &:hover {
        transform: translateY(-4px);
        box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
      }

      &.stat-card-primary {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;

        .stat-label {
          color: rgba(255, 255, 255, 0.9);
        }
      }

      &.stat-card-success {
        background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
        color: white;

        .stat-label {
          color: rgba(255, 255, 255, 0.9);
        }
      }

      &.stat-card-warning {
        background: linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%);
        color: #8b5cf6;

        .stat-label {
          color: rgba(139, 92, 246, 0.8);
        }
      }

      &.stat-card-info {
        background: linear-gradient(135deg, #a8edea 0%, #fed6e3 100%);
        color: #6366f1;

        .stat-label {
          color: rgba(99, 102, 241, 0.8);
        }
      }

      .stat-card-content {
        padding: 1.25rem;
        position: relative;
        z-index: 2;

        .stat-number {
          font-size: 2rem;
          font-weight: 800;
          line-height: 1;
          margin-bottom: 0.25rem;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .stat-label {
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.1em;
        }

        .stat-icon {
          position: absolute;
          top: 1rem;
          right: 1rem;

          .icon-wrapper {
            width: 40px;
            height: 40px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            backdrop-filter: blur(10px);
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

            i {
              font-size: 1.2rem;
            }

            &.icon-primary {
              background: linear-gradient(135deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.1));
              color: white;
            }

            &.icon-success {
              background: linear-gradient(135deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.1));
              color: white;
            }

            &.icon-warning {
              background: linear-gradient(135deg, rgba(139, 92, 246, 0.3), rgba(139, 92, 246, 0.1));
              color: #8b5cf6;
            }

            &.icon-info {
              background: linear-gradient(135deg, rgba(99, 102, 241, 0.3), rgba(99, 102, 241, 0.1));
              color: #6366f1;
            }
          }
        }
      }

      // Add decorative elements
      &::before {
        content: '';
        position: absolute;
        top: -50%;
        right: -50%;
        width: 100%;
        height: 100%;
        background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
        z-index: 1;
      }
    }
  }

  // Filters Section
  .filters-section {
    .box {
      border-radius: 16px;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
      background: rgba(255, 255, 255, 0.8);
      backdrop-filter: blur(10px);
      border: 1px solid rgba(255, 255, 255, 0.2);
      transition: all 0.3s ease;

      &:hover {
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
        background: rgba(255, 255, 255, 0.9);
      }
    }

    .tags {
      gap: 0.5rem;
    }
  }

  // Bulk Actions
  .bulk-actions-section {
    .notification {
      border-radius: 16px;
      border-left: 4px solid #3273dc;
      box-shadow: 0 4px 20px rgba(50, 115, 220, 0.15);
      background: linear-gradient(135deg, rgba(50, 115, 220, 0.05), rgba(50, 115, 220, 0.02));
    }
  }

  // View Controls
  .view-controls {
    display: flex;
    justify-content: flex-end;

    .buttons {
      .button {
        border-radius: 8px;
        transition: all 0.2s ease;
        background: white;
        border: 1px solid #d1d5db;
        color: #374151;

        &:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          border-color: #9ca3af;
        }

        &.is-primary {
          border-color: #667eea;
          color: #667eea;

          &:hover {
            background-color: #667eea;
            color: white;
          }
        }
      }
    }
  }

  // Loading State
  .loader-container {
    padding: 3rem 0;

    .loader {
      margin: 0 auto;
    }
  }

  // Empty State
  .empty-state {
    background: white !important;
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.05);
    margin: 2rem 0;
    min-height: 400px;
    display: flex;
    align-items: center;
    justify-content: center;

    .empty-state-content {
      padding: 3rem 2rem;
      width: 100%;
      max-width: 500px;
    }

    .empty-state-icon {
      font-size: 4rem;
      color: #d1d5db;
      margin-bottom: 1.5rem;
    }

    .title {
      color: #374151 !important;
      margin-bottom: 1rem !important;
      font-size: 1.5rem;
      font-weight: 600;
    }

    .subtitle {
      color: #6b7280 !important;
      margin-bottom: 2rem !important;
      font-size: 1rem;
      line-height: 1.5;
    }

    .buttons {
      margin-bottom: 0;
      gap: 0.75rem;

      .button {
        border-radius: 8px;
        transition: all 0.2s ease;
        font-weight: 500;
        padding: 0.75rem 1.5rem;

        &.is-light {
          background: white;
          border: 1px solid #d1d5db;
          color: #374151;

          &:hover {
            transform: translateY(-1px);
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            border-color: #9ca3af;
          }
        }

        &.is-primary {
          background-color: #667eea;
          border-color: #667eea;
          color: white;

          &:hover {
            background-color: #5a67d8;
            border-color: #5a67d8;
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
          }
        }
      }
    }
  }

  // User Cards
  .users-cards {
    .user-card {
      background: white;
      border-radius: 16px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      height: 100%;
      border: 2px solid transparent;
      position: relative;
      overflow: hidden;

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 4px;
        background: linear-gradient(90deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
        opacity: 0;
        transition: opacity 0.3s ease;
      }

      &:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);

        &::before {
          opacity: 1;
        }
      }

      &.is-selected {
        border-color: #667eea;
        box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.15), 0 8px 30px rgba(0, 0, 0, 0.12);

        &::before {
          opacity: 1;
        }
      }

      &.is-disabled {
        background-color: #fef2f2;
        border-color: #fca5a5;
        border-width: 2px;
        pointer-events: auto;

        .user-card-content {
          pointer-events: auto;

          .user-header {
            .user-name {
              color: #7f1d1d;
            }

            .user-email {
              color: #991b1b;
            }
          }

          .user-details {
            .detail-label {
              color: #7f1d1d;
            }

            .detail-value {
              color: #991b1b;
            }
          }
        }

        .user-card-actions {
          background-color: #fee2e2;
          pointer-events: auto;

          .button {
            pointer-events: auto;
            cursor: pointer;
            opacity: 1 !important;

            &.is-success {
              border-color: #10b981;
              color: #10b981;
              font-weight: 600;
              background-color: white;

              &:hover {
                background-color: #10b981 !important;
                color: white !important;
                transform: translateY(-1px);
                box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
              }
            }

            &.is-primary, &.is-info {
              opacity: 1;
              pointer-events: auto;

              &:hover {
                transform: translateY(-1px);
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
              }
            }
          }
        }

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(239, 68, 68, 0.15);
        }
      }

      .user-card-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 1rem 1rem 0;

        .user-card-checkbox {
          .checkbox {
            margin: 0;
          }
        }
      }

      .user-card-content {
        padding: 1.5rem;

        .user-header {
          display: flex;
          align-items: center;
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid #e5e7eb;

          .user-avatar-icon {
            position: relative;
            margin-right: 1rem;

            .user-badge {
              position: absolute;
              bottom: -2px;
              right: -2px;
              width: 20px;
              height: 20px;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              border: 2px solid white;

              &.superadmin-badge {
                background-color: #f59e0b;
                color: white;
              }

              &.admin-badge {
                background-color: #3b82f6;
                color: white;
              }

              .icon {
                font-size: 0.7rem;
              }
            }
          }

          .user-basic-info {
            flex: 1;

            .user-name {
              font-size: 1.1rem;
              font-weight: 600;
              color: #1f2937;
              margin-bottom: 0.25rem;

              .middle-name {
                font-weight: 400;
                opacity: 0.8;
              }
            }

            .user-email {
              color: #6b7280;
              font-size: 0.875rem;
            }
          }
        }

        .user-details {
          .user-detail-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 0.75rem;

            &:last-child {
              margin-bottom: 0;
            }

            .detail-label {
              display: flex;
              align-items: center;
              font-size: 0.8rem;
              color: #6b7280;
              font-weight: 500;

              .icon {
                margin-right: 0.5rem;
              }
            }

            .detail-value {
              color: #374151;
              font-weight: 500;
            }
          }
        }
      }

      .user-card-actions {
        padding: 1rem 1.5rem 1.5rem;
        border-top: 1px solid #e5e7eb;
        margin-top: 1rem;
        background-color: #f9fafb;

        .buttons {
          gap: 0.5rem;
          margin-bottom: 0;

          .button {
            flex: 1;
            border-radius: 8px;
            font-weight: 500;
            transition: all 0.2s ease;
            background: white;
            border: 1px solid #d1d5db;
            color: #374151;

            &.is-small {
              font-size: 0.75rem;
              padding: 0.5rem 1rem;
            }

            &:hover {
              transform: translateY(-1px);
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
              border-color: #9ca3af;
            }

            &.is-primary {
              border-color: #667eea;
              color: #667eea;

              &:hover {
                background-color: #667eea;
                color: white;
              }
            }

            &.is-info {
              border-color: #06b6d4;
              color: #06b6d4;

              &:hover {
                background-color: #06b6d4;
                color: white;
              }
            }

            &.is-success {
              border-color: #10b981;
              color: #10b981;

              &:hover {
                background-color: #10b981;
                color: white;
              }
            }

            &.is-warning {
              border-color: #f59e0b;
              color: #f59e0b;

              &:hover {
                background-color: #f59e0b;
                color: white;
              }
            }
          }
        }
      }
    }
  }

  // Table View
  .users-table {
    background: white;
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    overflow: hidden;
    border: 1px solid rgba(0, 0, 0, 0.05);

    .table {
      margin: 0;

      tr {
        transition: all 0.2s ease;

        &.is-selected {
          background: linear-gradient(135deg, rgba(102, 126, 234, 0.08), rgba(102, 126, 234, 0.04));
        }

        &:hover:not(.is-selected) {
          background-color: rgba(0, 0, 0, 0.02);
        }
      }

      th {
        background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
        border-bottom: 2px solid #e9ecef;
        font-weight: 700;
        color: #495057;
        padding: 1rem;
        text-transform: uppercase;
        font-size: 0.75rem;
        letter-spacing: 0.05em;
      }

      td {
        vertical-align: middle;
        border-bottom: 1px solid rgba(0, 0, 0, 0.05);
        padding: 1rem;
      }

      .user-cell {
        display: flex;
        align-items: center;

        .user-avatar-table {
          position: relative;
          flex-shrink: 0;

          .user-badge-small {
            position: absolute;
            bottom: -1px;
            right: -1px;
            width: 14px;
            height: 14px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            border: 1px solid white;
            font-size: 0.6rem;

            &.superadmin-badge-small {
              background-color: #f59e0b;
              color: white;
            }

            &.admin-badge-small {
              background-color: #3b82f6;
              color: white;
            }
          }
        }

        .user-name {
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 0.125rem;
        }

        .user-email {
          color: #6b7280;
          font-size: 0.875rem;
        }
      }

      .buttons {
        justify-content: center;
        margin: 0;
        gap: 0.25rem;
        display: flex;

        .button {
          margin: 0.125rem;
          border-radius: 8px;
          font-weight: 500;
          transition: all 0.2s ease;
          background: white;
          border: 1px solid #d1d5db;
          color: #374151;
          flex: 1;
          min-width: 100px;

          &.is-small {
            font-size: 0.75rem;
            padding: 0.375rem 0.75rem;
          }

          &:hover {
            transform: translateY(-1px);
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            border-color: #9ca3af;
          }

          &.is-primary {
            border-color: #667eea;
            color: #667eea;

            &:hover {
              background-color: #667eea;
              color: white;
            }
          }

          &.is-info {
            border-color: #06b6d4;
            color: #06b6d4;

            &:hover {
              background-color: #06b6d4;
              color: white;
            }
          }

          &.is-success {
            border-color: #10b981;
            color: #10b981;

            &:hover {
              background-color: #10b981;
              color: white;
            }
          }

          &.is-warning {
            border-color: #f59e0b;
            color: #f59e0b;

            &:hover {
              background-color: #f59e0b;
              color: white;
            }
          }
        }
      }
    }
  }

  // Pagination
  .pagination-section {
    .pagination {
      margin-bottom: 1rem;

      .pagination-link {
        border-radius: 6px;

        &.is-current {
          background-color: #3273dc;
          border-color: #3273dc;
        }
      }
    }

    .pagination-info {
      color: #6b7280;
    }
  }
}

// Responsive Design
@media screen and (max-width: 768px) {
  .users-page {
    .stats-section .columns {
      .column {
        padding: 0.5rem;
      }
    }

    .filters-section {
      .columns {
        .column {
          padding: 0.5rem;
        }
      }

      .box {
        padding: 1rem;
      }
    }

    .users-cards {
      .column {
        padding: 0.5rem;
      }

      .user-card {
        .user-card-actions .buttons {
          .button {
            font-size: 0.7rem;
            padding: 0.25rem 0.5rem;
          }
        }
      }
    }

    .users-table {
      .table-container {
        overflow-x: auto;
      }

      .table {
        .user-cell {
          .image {
            width: 24px !important;
            height: 24px !important;
            margin-right: 0.5rem !important;
          }

          .user-name {
            font-size: 0.875rem;
          }

          .user-email {
            font-size: 0.75rem;
          }
        }

        .buttons {
          .button {
            padding: 0.25rem;

            .icon {
              margin: 0 !important;
            }

            span:not(.icon) {
              display: none;
            }
          }
        }
      }
    }
  }
}

// Dark mode support
@media (prefers-color-scheme: dark) {
  .users-page {
    .stat-card,
    .filters-section .box,
    .empty-state,
    .user-card {
      background-color: #1f2937;
      color: #f9fafb;
    }

    .stat-card .stat-card-content .stat-label,
    .user-card .user-meta .user-meta-item {
      color: #9ca3af;
    }

    // Keep table view light even in dark mode
    .users-table {
      background-color: white;
      color: #1f2937;

      .table {
        background-color: white;
        color: #1f2937;

        tr.is-selected {
          background-color: rgba(102, 126, 234, 0.08);
        }

        th {
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          color: #495057;
          border-bottom-color: #e9ecef;
        }

        td {
          color: #1f2937;
          border-bottom-color: rgba(0, 0, 0, 0.05);
        }

        .user-name {
          color: #1f2937;
        }

        .user-email {
          color: #6b7280;
        }
      }
    }
  }
}

// Print styles
@media print {
  .users-page {
    .page-header,
    .filters-section,
    .bulk-actions-section,
    .view-controls,
    .user-card-actions,
    .pagination-section {
      display: none;
    }

    .users-cards .user-card {
      box-shadow: none;
      border: 1px solid #ccc;
      break-inside: avoid;
    }

    .users-table {
      box-shadow: none;

      .table {
        .buttons {
          display: none;
        }
      }
    }
  }
}
</style>